import * as React from "react";
import { Link } from "react-router-dom";
import { CircleCheck, CircleDashed, Loader } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CalibrationSubNav } from "@/routes/calibration/CalibrationSubNav";
import {
  caseAccess,
  isBoardComplete,
  lockedReason,
  nextCase,
  type CaseAccess,
} from "@/routes/calibration/case-access";
import { useCalibrationStatus } from "@/hooks/queries/useCalibrationStatus";
import type {
  CalibrationCaseData,
  CalibrationCaseStatus,
  CalibrationStatusData,
} from "@/hooks/queries/calibration-status-types";

// Story 8.7 (gh-246) — Calibration case-list (UX Screen 16). Hydrates from the FINAL
// GET /calibration/status wire (seam-81): `completed_cases` + `cases[] {case_id,
// order, status}`. Today the backend returns every case as `pending` / 0 (Story 8.3
// fills real derivations WITHOUT changing the wire), but the UI already handles all
// three statuses.
//
// Deliberate deviations from the mockup (checkpoint 2026-07-20 — do NOT re-add):
//   • no per-case task-label meta ("Tasks: F-1 - …") on the CARD — the board wire now
//     carries `task_code` (Story 8.10 D5), but a display NAME still needs the amps_tasks
//     join per card; that card-meta render is `seam-87-case-task-labels` (AR30-gated),
//     which stays open. The scoring view (Screen 17) DOES join the label for its header.
//   • no "last saved …" timestamp on the in-progress card — an 8.3 submission concern.
// Card CTAs route to the LIVE flow (Story 8.10 removed the placeholders): unscored →
// the scoring view (Screen 17, reuses Story 6.5 per FR41); scored → the read-only log
// (Screen 18, Story 8.8). Tokens only; `tabular-nums` on the KPIs; WCAG 2.1 AA (pills
// carry TEXT, colour is redundant — the ReadinessStatusCard precedent).

const TOTAL_CASES = 5;

// `nextCase` + `isBoardComplete` live in case-access.ts (the board and the scoring
// screen share one derivation; keeping them here would make the modules cyclic).
// Re-exported so their existing importers — and the board's pure tests — are untouched.
export { nextCase, isBoardComplete };

/**
 * The "Next case" KPI label. "Case {order}" for the next unscored case by `order`;
 * "All complete" ONLY when completion is actually proven (≥1 case AND every case
 * `scored`); "—" otherwise. `nextCase` returns null both when all cases are scored
 * AND when the board is empty/malformed — mapping that null straight to "All
 * complete" would let an empty `cases[]` render `0 / 5` + "All complete" (a
 * contradictory KPI pair). Completion is not "no unscored case found"; it is "every
 * case scored". Pure + testable.
 */
export function nextCaseLabel(cases: CalibrationCaseData[]): string {
  const next = nextCase(cases);
  if (next) return `Case ${next.order}`;
  return cases.length > 0 ? "All complete" : "—";
}

interface CasePresentation {
  pillLabel: string;
  pillVariant: React.ComponentProps<typeof Badge>["variant"];
  Icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean | "true" | "false" }>;
  ctaLabel: (order: number) => string;
  ctaTo: (caseId: string) => string;
  ctaVariant: React.ComponentProps<typeof Button>["variant"];
  /** The mockup's `.active` emphasis — only the in-progress card. */
  active: boolean;
}

const PRESENTATION: Record<CalibrationCaseStatus, CasePresentation> = {
  scored: {
    pillLabel: "Scored",
    pillVariant: "ok",
    Icon: CircleCheck,
    ctaLabel: () => "View case log",
    ctaTo: (caseId) => `/calibration/cases/${caseId}/log`,
    ctaVariant: "secondary",
    active: false,
  },
  in_progress: {
    pillLabel: "In progress",
    pillVariant: "info",
    Icon: Loader,
    ctaLabel: (order) => `Resume case ${order}`,
    ctaTo: (caseId) => `/calibration/cases/${caseId}`,
    ctaVariant: "primary",
    active: true,
  },
  pending: {
    pillLabel: "Pending",
    pillVariant: "neutral",
    Icon: CircleDashed,
    ctaLabel: (order) => `Start case ${order}`,
    ctaTo: (caseId) => `/calibration/cases/${caseId}`,
    ctaVariant: "secondary",
    active: false,
  },
};

function CaseCard({
  data,
  access,
  reason,
}: {
  data: CalibrationCaseData;
  access: CaseAccess;
  reason: string;
}) {
  const p = PRESENTATION[data.status];
  // Story 8.10 AC3 — sequential start. An unscored case that is not the next one
  // is inert: a react-router <Link> cannot be natively disabled, so a locked card
  // renders a real disabled <button> rather than a link styled to look dead.
  const locked = access === "locked";
  return (
    <li
      className={cn(
        "flex flex-col gap-3 rounded-2xl border bg-[color:var(--bg-primary)] p-5",
        p.active
          ? "border-[color:var(--border-brand-alt)] shadow-elevated"
          : "border-[color:var(--border-primary)]",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <p.Icon
            aria-hidden="true"
            className="h-5 w-5 shrink-0 text-[color:var(--text-tertiary)]"
          />
          <strong className="text-md font-semibold text-[color:var(--text-primary)]">
            Case {data.order}
          </strong>
        </div>
        {/* Colour is redundant reinforcement; the pill text carries the state. */}
        <Badge variant={locked ? "neutral" : p.pillVariant}>
          {locked ? "Locked" : p.pillLabel}
        </Badge>
      </div>
      <div className="flex flex-col gap-2">
        {locked ? (
          <Button variant="secondary" size="sm" type="button" disabled aria-disabled="true">
            {p.ctaLabel(data.order)}
          </Button>
        ) : (
          <Button asChild variant={p.ctaVariant} size="sm">
            <Link to={p.ctaTo(data.case_id)}>{p.ctaLabel(data.order)}</Link>
          </Button>
        )}
        {/* The reason is visible TEXT, never a native `title` — `title` is
            mouse-only and unreachable on the iPad PWA, the primary clinical
            surface (the standing gh-555/gh-568 finding). */}
        {locked ? (
          <p className="text-sm text-[color:var(--text-tertiary)]">{reason}</p>
        ) : null}
      </div>
    </li>
  );
}

function Kpi({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-5 py-4">
      <div className="text-sm text-[color:var(--text-tertiary)]">{label}</div>
      <div className="tabular-nums text-2xl font-semibold text-[color:var(--text-primary)]">
        {value}
      </div>
    </div>
  );
}

function CasesBody({ data }: { data: CalibrationStatusData }) {
  const completed = Math.max(0, Math.min(TOTAL_CASES, data.completed_cases));
  const ordered = [...data.cases].sort((a, b) => a.order - b.order);
  // One reason for every locked card — they are all blocked by the same case.
  const reason = lockedReason(data.cases);

  return (
    <>
      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        role="group"
        aria-label="Calibration summary"
      >
        <Kpi label="Cases completed" value={`${completed} / ${TOTAL_CASES}`} />
        {/* Story 8.8 AC6 — the all-scored completed affordance links to the
            completion screen (Screen 19). Auto-navigation after the 5th score is
            the scoring-view story's concern, not the board's. */}
        <Kpi
          label="Next case"
          value={
            isBoardComplete(data.cases) ? (
              <Link
                to="/calibration/complete"
                className="text-[color:var(--text-brand-secondary)] underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)]"
              >
                All complete
              </Link>
            ) : (
              nextCaseLabel(data.cases)
            )
          }
        />
      </div>

      <ul className="flex flex-col gap-3" aria-label="Calibration cases">
        {ordered.map((c) => (
          <CaseCard
            key={c.case_id}
            data={c}
            access={caseAccess(data.cases, c.case_id)}
            reason={reason}
          />
        ))}
      </ul>
    </>
  );
}

export function CalibrationCasesScreen() {
  // Reuse the Story 8.1 hook + key (["calibration-status", clinicianId]); SELF-only
  // read here, so clinicianId is undefined → key ["calibration-status", undefined].
  const { data, isPending } = useCalibrationStatus({ enabled: true });

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="display-xs text-[color:var(--text-primary)]">Case Studies</h1>
        <p className="text-sm text-[color:var(--text-tertiary)]">
          Open each calibration case to score it against the gold-standard reference.
        </p>
      </header>

      <CalibrationSubNav />

      {/* AC8 posture. While the read is PENDING the board is quiet — no placeholder
          box (the ReadinessStatusCard precedent). But this is a dedicated DESTINATION,
          not a home widget that can vanish: a failed/absent read renders a calm,
          non-alarming Banner (never a misleading empty board that reads as "still
          loading"), and never a retry affordance — the hook is offline-aware
          (networkMode "always", retry false) and the BE gate is the scoring authority. */}
      {isPending ? null : data ? (
        <CasesBody data={data} />
      ) : (
        <Banner variant="info" title="Case board unavailable">
          <p>
            We couldn’t load your calibration cases just now. Your progress is safe —
            please try again in a moment.
          </p>
        </Banner>
      )}
    </section>
  );
}

export default CalibrationCasesScreen;
